import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { apiErr } from "@/lib/api";
import { Container, PageHeader, Spinner } from "@/components/shared";
import ScrollRow from "@/components/ScrollRow";
import { toast } from "sonner";
import { BookMarked } from "lucide-react";

function Cover({ b, onClick, t }) {
  return (
    <button data-testid={`book-card-${b.book_id}`} onClick={onClick} className="group snap-start shrink-0 w-32 md:w-36 text-left">
      <div className="relative overflow-hidden rounded-xl border border-white/10 aspect-[2/3] bg-white/[0.03]">
        {b.cover_url
          ? <img src={b.cover_url} alt={b.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.05]" />
          : <div className="w-full h-full flex items-center justify-center"><BookMarked className="text-white/20" size={30} /></div>}
        {b.partner && <span className="absolute top-1.5 left-1.5 text-[9px] font-medium bg-black/75 border border-gold/40 text-gold rounded-full px-1.5 py-0.5">{t("pages.books.partnerAuthor")}</span>}
        {!b.partner && b.featured && <span className="absolute top-1.5 left-1.5 text-[9px] font-medium bg-black/75 border border-gold/40 text-gold rounded-full px-1.5 py-0.5">{t("pages.books.recommended")}</span>}
      </div>
      <p className="text-white/80 text-xs mt-2 leading-tight group-hover:text-gold transition-colors line-clamp-2">{b.title}</p>
      <p className="text-white/35 text-[11px] line-clamp-1">{b.author}</p>
    </button>
  );
}

export default function Books() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [books, setBooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cat, setCat] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/books"); setBooks(data.books); setCategories(data.categories); }
    catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = (b) => navigate(`/bibliotheque/${b.book_id}`);
  const filtered = cat ? books.filter((b) => (b.categories || []).includes(cat)) : books;
  const catMeta = categories.find((c) => c.key === cat);
  const nonEmpty = categories.filter((c) => c.count > 0);
  // With few books the per-category carousels would just repeat the same covers.
  // Show a clean grid until the catalogue is large enough for the Apple-Books showroom.
  const showroom = books.length >= 4;

  return (
    <Container>
      <PageHeader eyebrow={t("pages.books.eyebrow")} title={t("pages.books.title")}
        subtitle={t("pages.books.subtitle")} />

      <div className="flex flex-wrap gap-2 mb-8" data-testid="books-categories">
        <button data-testid="books-cat-all" onClick={() => setCat("")}
          className={`text-sm rounded-full px-3.5 py-1.5 border transition-colors ${!cat ? "border-gold/50 bg-gold/10 text-gold" : "border-white/12 text-white/60 hover:text-white"}`}>
          {t("pages.books.all")} ({books.length})
        </button>
        {categories.map((c) => (
          <button key={c.key} data-testid={`books-cat-${c.key}`} onClick={() => setCat(c.key)}
            className={`text-sm rounded-full px-3.5 py-1.5 border transition-colors ${cat === c.key ? "border-gold/50 bg-gold/10 text-gold" : "border-white/12 text-white/60 hover:text-white"}`}>
            {c.emoji} {c.label} ({c.count})
          </button>
        ))}
      </div>

      {loading && <Spinner label={t("pages.books.loading")} />}

      {/* Vue filtrée : grille */}
      {!loading && cat && (
        filtered.length === 0 ? (
          <div className="card-surface p-12 text-center" data-testid="books-empty">
            <BookMarked size={30} className="text-gold/60 mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-white/55">{t("pages.books.emptyCat", { cat: catMeta?.label })}</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-5" data-testid="books-grid">
            {filtered.map((b) => <Cover key={b.book_id} b={b} onClick={() => open(b)} t={t} />)}
          </div>
        )
      )}

      {/* Vue vitrine : rangées par catégorie (façon Apple Books) quand le catalogue est assez fourni */}
      {!loading && !cat && showroom && (
        <div className="space-y-10" data-testid="books-showroom">
          <section data-testid="books-row-nouveautes">
            <h2 className="font-display text-lg md:text-xl tracking-tight mb-3">{t("pages.books.nouveautes")}</h2>
            <ScrollRow testId="books-row-nouveautes-scroll">
              {books.map((b) => <Cover key={b.book_id} b={b} onClick={() => open(b)} t={t} />)}
            </ScrollRow>
          </section>
          {nonEmpty.map((c) => (
            <section key={c.key} data-testid={`books-row-${c.key}`}>
              <h2 className="font-display text-lg md:text-xl tracking-tight mb-3">{c.emoji} {c.label}</h2>
              <ScrollRow testId={`books-row-${c.key}-scroll`}>
                {books.filter((b) => (b.categories || []).includes(c.key)).map((b) => <Cover key={b.book_id} b={b} onClick={() => open(b)} t={t} />)}
              </ScrollRow>
            </section>
          ))}
        </div>
      )}

      {/* Catalogue restreint : grille simple et élégante (pas de répétition des mêmes couvertures) */}
      {!loading && !cat && !showroom && books.length > 0 && (
        <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-5" data-testid="books-grid">
          {books.map((b) => <Cover key={b.book_id} b={b} onClick={() => open(b)} t={t} />)}
        </div>
      )}

      {!loading && !cat && books.length === 0 && (
        <div className="card-surface p-12 text-center" data-testid="books-empty">
          <BookMarked size={30} className="text-gold/60 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-white/55">{t("pages.books.empty")}</p>
        </div>
      )}
    </Container>
  );
}
